import type { AuthResponse, EmailUndoResult, LinkedAccount, LoginRequest, RegisterRequest, RegisterResponse, RefreshRequest, MeResponse, PasskeyCredential, SignedInDevice, User } from './types.ts'
import { apiBaseUrl, apiFetch, problemMessage } from './client.ts'
import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON, RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser'


export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function loginApi(data: LoginRequest): Promise<AuthResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Login failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function registerApi(data: RegisterRequest & { redirect_url?: string; redirect?: string }): Promise<RegisterResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Registration failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function refreshApi(data: RefreshRequest): Promise<AuthResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Token refresh failed' }))
    throw new ApiError(err.detail ?? err.title ?? err.error ?? 'Token refresh failed', res.status)
  }
  return res.json()
}

// Logout authenticates by refresh token (not the access token), so the server-side
// session is revoked even when the access token has expired — e.g. after a tab has
// been idle/asleep. Best-effort: the caller ignores failures and clears local state.
export async function logoutApi(sessionId: string, refreshToken: string): Promise<void> {
  await fetch(`${apiBaseUrl()}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ session_id: sessionId, refresh_token: refreshToken }),
  })
}

export async function getMeApi(accessToken: string): Promise<MeResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/me`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    throw new Error('Failed to fetch user')
  }
  return res.json()
}

export async function checkUsernameApi(username: string): Promise<{ available: boolean }> {
  const res = await fetch(`${apiBaseUrl()}/auth/check-username?username=${encodeURIComponent(username)}`)
  if (!res.ok) {
    throw new Error('Failed to check username')
  }
  return res.json()
}

export async function checkEmailApi(email: string): Promise<{ available: boolean }> {
  const res = await fetch(`${apiBaseUrl()}/auth/check-email?email=${encodeURIComponent(email)}`)
  if (!res.ok) {
    throw new Error('Failed to check email')
  }
  return res.json()
}

export async function getLinkedAccountsApi(accessToken: string): Promise<{ linked_accounts: LinkedAccount[] }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    throw new Error('Failed to fetch linked accounts')
  }
  return res.json()
}

export async function unlinkAccountApi(provider: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/${provider}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to unlink account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

// Multi-account: id-scoped operations. Use these instead of the provider-scoped
// variants when the user has (or may have) multiple accounts per provider.

export async function unlinkAccountByIDApi(accountID: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to unlink account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export async function setLinkedAccountLabelApi(accountID: string, label: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}/label`, {
    method: 'PATCH',
    headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to set label' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export async function archiveLinkedAccountApi(accountID: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}/archive`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to archive account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export async function unarchiveLinkedAccountApi(accountID: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}/unarchive`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to unarchive account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export interface GameAssociation {
  account_id: string
  game_id: string
  /** The player made this account their main for the game. */
  is_main?: boolean
}

export async function getGameAssociationsApi(accessToken: string): Promise<{ associations: GameAssociation[] }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/game-associations`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) throw new Error('Failed to fetch game associations')
  return res.json()
}

export async function linkAccountToGameApi(accountID: string, gameID: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}/games/${gameID}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to tag account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export async function unlinkAccountFromGameApi(accountID: string, gameID: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/by-id/${accountID}/games/${gameID}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to untag account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

export interface SupportedGame {
  id: string
  name: string
  slug: string
  publisher: string
  logo_url: string
  linked_account_provider?: string
}

export async function listGamesApi(): Promise<{ games: SupportedGame[] }> {
  const res = await fetch(`${apiBaseUrl()}/games`)
  if (!res.ok) throw new Error('Failed to fetch games')
  return res.json()
}

export async function manualLinkApi(provider: string, id: string, accessToken: string, region?: string): Promise<{ status: string; username: string }> {
  const body: Record<string, string | undefined> = { provider, region }
  if (provider === 'riot') {
    body.riot_id = id
  } else {
    body.username = id
  }
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/manual`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to link account' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export interface RiotChallengeResponse {
  challenge_icon_id: number
  challenge_icon_url: string
  expires_at: string
  instructions: string
}

export async function getRiotChallengeApi(accessToken: string): Promise<RiotChallengeResponse | null> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/riot/challenge`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (res.status === 404) return null
  if (!res.ok) return null
  return res.json()
}

export async function riotChallengeApi(accessToken: string): Promise<RiotChallengeResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/riot/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to create challenge' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function riotVerifyApi(accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/riot/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function phoneSendCodeApi(phoneNumber: string, accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/phone/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ phone_number: phoneNumber }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send code' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function phoneVerifyCodeApi(phoneNumber: string, code: string, accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/phone/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ phone_number: phoneNumber, code }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function phoneRemoveApi(accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/phone`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to remove phone' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function resendVerificationApi(email: string, redirectUrl?: string): Promise<{ status: string }> {
  const body: Record<string, string> = { email }
  if (redirectUrl) body.redirect_url = redirectUrl
  const res = await fetch(`${apiBaseUrl()}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to resend' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function verifyEmailApi(token: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Verification failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function getProvidersApi(): Promise<{ providers: string[]; riot_oauth: boolean }> {
  const res = await fetch(`${apiBaseUrl()}/auth/providers`)
  if (!res.ok) {
    throw new Error('Failed to fetch providers')
  }
  return res.json()
}

export async function forgotPasswordApi(email: string, redirectUrl?: string): Promise<{ status: string }> {
  const body: Record<string, string> = { email }
  if (redirectUrl) body.redirect_url = redirectUrl
  const res = await fetch(`${apiBaseUrl()}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to send reset email' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function resetPasswordApi(token: string, password: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Password reset failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function uploadAvatarApi(file: Blob, accessToken: string): Promise<{ avatar_url: string }> {
  const formData = new FormData()
  formData.append('avatar', file, 'avatar.jpg')
  const res = await fetch(`${apiBaseUrl()}/auth/avatar`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function removeAvatarApi(accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/avatar`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to remove avatar' }))
    throw new Error(problemMessage(err, res.status))
  }
}

// The profile banner: a wide image the client crops to 4:1 before upload.
// JPEG, PNG or WebP up to 8 MB (no GIF, unlike avatars).
export async function uploadBannerApi(file: Blob, accessToken: string): Promise<{ banner_url: string }> {
  const formData = new FormData()
  formData.append('banner', file, 'banner.jpg')
  const res = await fetch(`${apiBaseUrl()}/auth/banner`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function deleteBannerApi(accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/banner`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to remove banner' }))
    throw new Error(problemMessage(err, res.status))
  }
}

// --- Passkey / WebAuthn ---

/**
 * Which relying party the API verifies a ceremony against. Web pages omit it;
 * the mobile app sends 'native' (RP ID carousel.gg, the app's origins) on every
 * begin and finish call — the two must agree.
 */
export type PasskeyClient = 'native'

function clientParam(client: PasskeyClient | undefined, sep: '?' | '&'): string {
  return client ? `${sep}client=${client}` : ''
}

export async function passkeyRegisterBeginApi(name: string, accessToken: string, client?: PasskeyClient): Promise<{ challenge_id: string; options: PublicKeyCredentialCreationOptionsJSON }> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/register/begin${clientParam(client, '?')}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ name }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to begin registration' }))
    throw new Error(problemMessage(err, res.status))
  }
  const data = await res.json()
  // go-webauthn wraps options in { publicKey: { ... } } but @simplewebauthn expects the inner object
  return { challenge_id: data.challenge_id, options: data.options.publicKey ?? data.options }
}

export async function passkeyRegisterFinishApi(challengeId: string, name: string, credential: RegistrationResponseJSON, accessToken: string, client?: PasskeyClient): Promise<{ credential: PasskeyCredential }> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/register/finish?challenge_id=${encodeURIComponent(challengeId)}&name=${encodeURIComponent(name)}${clientParam(client, '&')}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(credential),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Registration failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function passkeyLoginBeginApi(client?: PasskeyClient): Promise<{ challenge_id: string; options: PublicKeyCredentialRequestOptionsJSON }> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/login/begin${clientParam(client, '?')}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'No passkeys found' }))
    throw new Error(problemMessage(err, res.status))
  }
  const data = await res.json()
  return { challenge_id: data.challenge_id, options: data.options.publicKey ?? data.options }
}

export async function passkeyLoginFinishApi(challengeId: string, credential: AuthenticationResponseJSON, client?: PasskeyClient): Promise<AuthResponse> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/login/finish?challenge_id=${encodeURIComponent(challengeId)}${clientParam(client, '&')}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credential),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Passkey login failed' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function passkeyListCredentialsApi(accessToken: string): Promise<{ credentials: PasskeyCredential[] }> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/credentials`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    throw new Error('Failed to fetch passkeys')
  }
  return res.json()
}

export async function passkeyDeleteCredentialApi(id: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/passkey/credentials/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to delete passkey' }))
    throw new Error(problemMessage(err, res.status))
  }
}

// --- Terms & DOB ---

export async function acceptTermsApi(accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/accept-terms`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ terms_accepted: true }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to accept terms' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function submitDateOfBirthApi(dateOfBirth: string, parentalConsent: boolean, accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/date-of-birth`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ date_of_birth: dateOfBirth, parental_consent: parentalConsent }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to save date of birth' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

// --- Linked Account Visibility ---

export async function updateAccountVisibilityApi(accountId: string, visibility: 'public' | 'private', accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/${accountId}/visibility`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ visibility }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update visibility' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function updateCS2CodesApi(authCode: string, shareCode: string, accessToken: string): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/steam/cs2-codes`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ auth_code: authCode, share_code: shareCode }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to save CS2 codes' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function linkPUBGAccountApi(username: string, platform: string, accessToken: string): Promise<{ status: string; username: string; account_id: string; platform: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/pubg`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ username, platform }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to link PUBG account' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function verifyPUBGAccountApi(accessToken: string): Promise<{ status: string; message?: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/linked-accounts/pubg/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to verify PUBG account' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

// --- Self-service profile / account ---

// --- Newsletter subscription (logged-in user) ---

export async function getNewsletterSubscriptionApi(
  accessToken: string,
): Promise<{ subscribed: boolean; email: string }> {
  const res = await fetch(`${apiBaseUrl()}/newsletter/subscription`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to load subscription' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function setNewsletterSubscriptionApi(
  subscribe: boolean,
  accessToken: string,
): Promise<{ subscribed: boolean; email: string }> {
  const res = await fetch(`${apiBaseUrl()}/newsletter/subscription`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ subscribe }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update subscription' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function updateProfileApi(
  fields: { gamer_tag?: string; bio?: string },
  accessToken: string,
): Promise<{ user: User }> {
  const res = await fetch(`${apiBaseUrl()}/auth/me`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(fields),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to update profile' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function changePasswordApi(
  currentPassword: string,
  newPassword: string,
  accessToken: string,
): Promise<{ status: string }> {
  const res = await fetch(`${apiBaseUrl()}/auth/password/change`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to change password' }))
    throw new Error(problemMessage(err, res.status))
  }
  return res.json()
}

export async function deleteAccountApi(confirmation: string, accessToken: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/auth/me`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ confirmation }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Failed to delete account' }))
    throw new Error(problemMessage(err, res.status))
  }
}

// ─── Email change ─────────────────────────────────────────────────────────

/**
 * Moves the account to a new email at once. It stays unconfirmed, costing the
 * email trust points, until the link sent there is clicked. Needs the password.
 */
export function changeEmailApi(newEmail: string, password: string, redirectUrl?: string): Promise<{ user: User }> {
  return apiFetch('/auth/email/change', {
    method: 'POST',
    body: JSON.stringify({ new_email: newEmail, password, ...(redirectUrl ? { redirect_url: redirectUrl } : {}) }),
  })
}

/** Back to the confirmed email the account had before the unconfirmed one. */
export function goBackToPreviousEmailApi(): Promise<{ user: User }> {
  return apiFetch('/auth/email/go-back', { method: 'POST' })
}

async function publicPost<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: fallback }))
    throw new ApiError(problemMessage(err, res.status), res.status)
  }
  return res.json()
}

/** The undo link mailed to an old address. No sign-in needed: the link is the proof. */
export function undoEmailChangeApi(token: string): Promise<EmailUndoResult> {
  return publicPost('/auth/email-change/undo', { token }, 'Could not undo the change')
}

/**
 * "This wasn't me", after an undo: sets a new password and signs out every
 * session. Returns a fresh one for the device answering.
 */
export function secureAfterEmailChangeApi(token: string, password: string): Promise<AuthResponse> {
  return publicPost('/auth/email-change/secure', { token, password }, 'Could not secure your account')
}

// ─── Signed-in devices ────────────────────────────────────────────────────

/** Devices signed in to the account; `currentSessionId` marks this one. */
export function listDevicesApi(currentSessionId?: string | null): Promise<{ devices: SignedInDevice[] }> {
  const q = currentSessionId ? `?current=${encodeURIComponent(currentSessionId)}` : ''
  return apiFetch(`/auth/devices${q}`)
}

export function signOutDeviceApi(id: string): Promise<{ status: string }> {
  return apiFetch(`/auth/devices/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** Signs out every device but the one whose session this is. */
export function signOutOtherDevicesApi(currentSessionId: string): Promise<{ signed_out: number }> {
  return apiFetch('/auth/devices/sign-out-others', {
    method: 'POST',
    body: JSON.stringify({ current_session_id: currentSessionId }),
  })
}

// ─── Games & accounts, Public profile (account hub) ───────────────────────

/** Makes an account the one the player leads with for a game. */
export function setMainAccountApi(accountId: string, gameId: string): Promise<{ status: string }> {
  return apiFetch(`/auth/linked-accounts/by-id/${encodeURIComponent(accountId)}/games/${encodeURIComponent(gameId)}/main`, { method: 'POST' })
}

/** One game on the public profile, in the player's order. */
export interface ProfileGameSetting {
  game_id: string
  slug: string
  name: string
  logo_url: string
  hidden: boolean
}

/** What the player chose to show on their public profile. */
export interface ProfileSettings {
  region: string
  timezone: string
  show_orgs_run: boolean
  show_social_links: boolean
  discord_messages: boolean
  games: ProfileGameSetting[]
}

export interface ProfileSettingsUpdate {
  region?: string
  timezone?: string
  show_orgs_run?: boolean
  show_social_links?: boolean
  discord_messages?: boolean
  /** The whole list, in order. */
  games?: { game_id: string; hidden: boolean }[]
}

export function getProfileSettingsApi(): Promise<ProfileSettings> {
  return apiFetch('/auth/me/profile-settings')
}

export function updateProfileSettingsApi(update: ProfileSettingsUpdate): Promise<ProfileSettings> {
  return apiFetch('/auth/me/profile-settings', { method: 'PATCH', body: JSON.stringify(update) })
}

/** Makes a linked account's picture (Discord's, usually) the player's avatar. */
export function setAvatarFromAccountApi(accountId: string): Promise<{ avatar_url: string }> {
  return apiFetch(`/auth/avatar/from-account/${encodeURIComponent(accountId)}`, { method: 'POST' })
}
